import { FEATURE_REGION } from "@webscan/decide.js";
import { grayPlane } from "@webscan/signature.js";
import { CARD_H, CARD_W } from "@webscan/constants.js";
import { toU8 } from "@webscan/image.js";
import { assetUrl } from "./assets";

/** One group's reference features, as the worker's ORB matcher wants them. */
export interface Reference {
  count: number;
  keypoints: Float32Array;
  descriptors: Uint8Array;
}

/** 8 bytes of keypoint plus 32 of descriptor, per feature. */
const RECORD = 40;

/**
 * Bounded so a long session over a big table does not accumulate the whole file in
 * memory — which is the thing the range reads exist to avoid.
 */
const CACHE_LIMIT = 256;
const cache = new Map<number, Reference>();

/**
 * One group's reference features, fetched as a byte range out of the ~1 GB descriptor
 * file.
 *
 * The file is never downloaded. This is why the dev server answers `Range` requests, and
 * why a static host that ignores them degrades to no verification rather than to a
 * gigabyte transfer — the 206 status check below is what turns that into a clean failure.
 */
export async function referenceFeatures(
  spans: Uint32Array,
  row: number,
): Promise<Reference | null> {
  const hit = cache.get(row);
  if (hit) return hit;

  const offset = spans[row * 2];
  const count = spans[row * 2 + 1];
  if (!count) return null;

  const length = count * RECORD;
  const response = await fetch(assetUrl("descriptors.bin"), {
    headers: { Range: `bytes=${offset}-${offset + length - 1}` },
  });
  if (response.status !== 206) {
    throw new Error(`descriptors.bin: expected 206, got ${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength !== length) throw new Error("descriptors.bin: short range response");

  const reference: Reference = {
    count,
    keypoints: new Float32Array(buffer, 0, count * 2),
    descriptors: new Uint8Array(buffer, count * 8, count * 32),
  };
  if (cache.size > CACHE_LIMIT) cache.clear();
  cache.set(row, reference);
  return reference;
}

/**
 * The art box of a normalised card, as the grayscale the worker's ORB reads.
 *
 * Features come from the illustration alone, not the whole card: every card of a given
 * frame era shares its border, title bar and text box, and ORB latches onto that shared
 * geometry hard enough to match two completely different cards with a near-perfect
 * homography.
 */
export function artBoxGray(card: Uint8ClampedArray): Uint8Array {
  const { x0, y0, width, height } = FEATURE_REGION;
  return Uint8Array.from(toU8(grayPlane(card, CARD_W, CARD_H, x0, y0, x0 + width, y0 + height)));
}
